/* ==========================================================================
  the-menu.js (Ghost iOS PUSH submenus) — FAST CLICK SAFE
  - Unlocks via transitionend (no "lag lock")
  - Queues last navigation request while animating
============================================================================ */
(() => {
  if (window.__MD_DRAWER_INIT__) return;
  window.__MD_DRAWER_INIT__ = true;

  const SEL = {
    html: document.documentElement,
    drawer: 'menu-drawer.md-drawer[data-menu-drawer]',
    overlay: '.md-overlay',
    openers: '[data-menu-drawer-open]',
    closeBtn: '[data-action="close-menu"]',

    submenusWrap: '.md-submenus',
    submenu: '.md-submenu[data-submenu-id]',

    openSub: '[data-action="open-submenu"][data-submenu-target]',
    closeSub: '[data-action="close-submenu"][data-submenu-target]',

    focusables:
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const STATE = {
    open: false,
    locked: false,
    lastActive: null,
    closeTimer: null,

    animating: false,
    activeId: 'submenu-root',
    stack: ['submenu-root'],

    // while animating, store last intent
    queuedNav: null,
    unlockTimer: null
  };

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const getMsVar = (name, fallback) => {
    const raw = getComputedStyle(SEL.html).getPropertyValue(name).trim();
    if (!raw) return fallback;
    if (raw.endsWith('ms')) return parseFloat(raw) || fallback;
    if (raw.endsWith('s')) return (parseFloat(raw) * 1000) || fallback;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  };

  const setAriaHidden = (el, hidden) =>
    el?.setAttribute('aria-hidden', hidden ? 'true' : 'false');

  const lockScroll = (locked) => {
    const html = SEL.html;
    const cur = Number(html.dataset.drawerLockCount || 0);

    if (locked) {
      html.dataset.drawerLockCount = String(cur + 1);
      html.classList.add('drawer-lock');
    } else {
      const next = Math.max(0, cur - 1);
      html.dataset.drawerLockCount = String(next);
      if (next === 0) html.classList.remove('drawer-lock');
    }
  };

  const setHamburgerExpanded = (expanded) => {
    $$(SEL.openers).forEach((btn) =>
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false')
    );
  };

  const getSubmenu = (drawer, id) =>
    $$(SEL.submenu, drawer).find((n) => n.getAttribute('data-submenu-id') === id) || null;

  const setActiveInstant = (drawer, id) => {
    STATE.activeId = id;

    $$(SEL.submenu, drawer).forEach((p) => {
      const pid = p.getAttribute('data-submenu-id');
      const active = pid === id;

      p.classList.toggle('is-active', active);
      p.classList.remove('is-next', 'is-prev');

      setAriaHidden(p, !active);
    });
  };

  const getFocusableInActive = (drawer) => {
    const active = getSubmenu(drawer, STATE.activeId);
    if (!active) return [];
    return $$(SEL.focusables, active).filter((el) => {
      const disabled = el.matches('[disabled]') || el.getAttribute('aria-disabled') === 'true';
      return !disabled;
    });
  };

  const focusFirstInActive = (drawer) => {
    const focusables = getFocusableInActive(drawer);
    (focusables[0] || drawer)?.focus?.({ preventScroll: true });
  };

  const focusTrap = (drawer, e) => {
    if (!STATE.open || e.key !== 'Tab') return;

    const focusables = getFocusableInActive(drawer);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus?.({ preventScroll: true });
      return;
    }
    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus?.({ preventScroll: true });
    }
  };

  const resetToRoot = (drawer) => {
    STATE.stack = ['submenu-root'];

    $$(`${SEL.openSub}[aria-expanded="true"]`, drawer).forEach((b) =>
      b.setAttribute('aria-expanded', 'false')
    );

    setActiveInstant(drawer, 'submenu-root');
  };

  // ---------- FAST unlock logic ----------
  const clearUnlockTimer = () => {
    if (STATE.unlockTimer) {
      clearTimeout(STATE.unlockTimer);
      STATE.unlockTimer = null;
    }
  };

  const runQueuedIfAny = (drawer) => {
    const q = STATE.queuedNav;
    STATE.queuedNav = null;
    if (!q) return;

    // run last intent
    animatePush(drawer, q.targetId, q.dir, { fromQueue: true });
  };

  const unlock = (drawer) => {
    STATE.animating = false;
    clearUnlockTimer();
    runQueuedIfAny(drawer);
  };

  const animatePush = (drawer, targetId, dir /* 'forward'|'back' */, opts = {}) => {
    const wrap = $(SEL.submenusWrap, drawer);
    const from = getSubmenu(drawer, STATE.activeId);
    const to = getSubmenu(drawer, targetId);

    if (!wrap || !from || !to) return;
    if (targetId === STATE.activeId) return;

    // If we're animating, queue last intent and bail.
    if (STATE.animating) {
      STATE.queuedNav = { targetId, dir };
      return;
    }

    STATE.animating = true;

    // stage reset
    wrap.classList.remove('is-animating-forward', 'is-animating-back');

    // clear prep markers
    $$(SEL.submenu, drawer).forEach((p) => p.classList.remove('is-next', 'is-prev'));

    // ensure target is visible for animation
    setAriaHidden(to, false);

    // prep incoming position
    if (dir === 'forward') to.classList.add('is-next');
    else to.classList.add('is-prev');

    // force paint then animate
    wrap.getBoundingClientRect();
    wrap.classList.add(dir === 'forward' ? 'is-animating-forward' : 'is-animating-back');

    // Unlock on transitionend of the incoming panel transform
    const onEnd = (e) => {
      if (e.propertyName !== 'transform') return;
      to.removeEventListener('transitionend', onEnd);

      // cleanup / commit
      wrap.classList.remove('is-animating-forward', 'is-animating-back');

      $$(SEL.submenu, drawer).forEach((p) => {
        const pid = p.getAttribute('data-submenu-id');
        const active = pid === targetId;

        p.classList.toggle('is-active', active);
        p.classList.remove('is-next', 'is-prev');
        setAriaHidden(p, !active);
      });

      STATE.activeId = targetId;

      focusFirstInActive(drawer);
      unlock(drawer);
    };

    // attach listener before any possible immediate end
    to.addEventListener('transitionend', onEnd);

    // Fallback unlock (in case iOS drops transitionend)
    clearUnlockTimer();
    const dur = getMsVar('--md-submenu-dur', getMsVar('--md-track-dur', 620));
    STATE.unlockTimer = setTimeout(() => {
      to.removeEventListener('transitionend', onEnd);

      // cleanup / commit (same as onEnd)
      wrap.classList.remove('is-animating-forward', 'is-animating-back');

      $$(SEL.submenu, drawer).forEach((p) => {
        const pid = p.getAttribute('data-submenu-id');
        const active = pid === targetId;

        p.classList.toggle('is-active', active);
        p.classList.remove('is-next', 'is-prev');
        setAriaHidden(p, !active);
      });

      STATE.activeId = targetId;
      focusFirstInActive(drawer);
      unlock(drawer);
    }, dur + 120);
  };

  // ---------- Drawer open/close ----------
  const openDrawer = (drawer, overlay) => {
    if (STATE.open) return;

    if (STATE.closeTimer) {
      clearTimeout(STATE.closeTimer);
      STATE.closeTimer = null;
      drawer.classList.remove('md-closing');
      overlay.classList.remove('md-closing');
    }

    STATE.lastActive = document.activeElement;
    STATE.open = true;

    SEL.html.setAttribute('data-md-ready', 'true');
    drawer.style.visibility = '';

    resetToRoot(drawer);

    setAriaHidden(drawer, false);
    setAriaHidden(overlay, false);

    if (!STATE.locked) {
      lockScroll(true);
      STATE.locked = true;
    }

    setHamburgerExpanded(true);

    drawer.getBoundingClientRect();

    requestAnimationFrame(() => {
      drawer.classList.add('md-open');
      overlay.classList.add('md-open');
      requestAnimationFrame(() => focusFirstInActive(drawer));
    });
  };

  const closeDrawer = (drawer, overlay) => {
    if (!STATE.open) return;

    STATE.open = false;
    setHamburgerExpanded(false);

    drawer.classList.remove('md-open');
    overlay.classList.remove('md-open');

    drawer.classList.add('md-closing');
    overlay.classList.add('md-closing');

    const dur = Math.max(getMsVar('--md-close', 950), getMsVar('--md-overlay-dur', 450));

    STATE.closeTimer = window.setTimeout(() => {
      drawer.classList.remove('md-closing');
      overlay.classList.remove('md-closing');

      setAriaHidden(drawer, true);
      setAriaHidden(overlay, true);

      if (STATE.locked) {
        lockScroll(false);
        STATE.locked = false;
      }

      STATE.lastActive?.focus?.({ preventScroll: true });
      STATE.lastActive = null;

      STATE.animating = false;
      STATE.queuedNav = null;
      clearUnlockTimer();

      resetToRoot(drawer);
      STATE.closeTimer = null;
    }, dur + 30);
  };

  const backOne = (drawer) => {
    if (STATE.stack.length <= 1) return;

    const fromId = STATE.stack.pop();
    const targetId = STATE.stack[STATE.stack.length - 1] || 'submenu-root';

    const opener = $$(`${SEL.openSub}[data-submenu-target="${CSS.escape(fromId)}"][aria-expanded="true"]`, drawer)[0];
    opener?.setAttribute('aria-expanded', 'false');

    animatePush(drawer, targetId, 'back');
  };

  const init = () => {
    const drawer = $(SEL.drawer);
    const overlay = $(SEL.overlay);
    if (!drawer || !overlay) return;

    if (!$(SEL.submenusWrap, drawer)) return;
    if (!getSubmenu(drawer, 'submenu-root')) return;

    drawer.classList.remove('md-open', 'md-closing');
    overlay.classList.remove('md-open', 'md-closing');

    setAriaHidden(drawer, true);
    setAriaHidden(overlay, true);

    setHamburgerExpanded(false);

    SEL.html.setAttribute('data-md-ready', 'true');
    drawer.style.visibility = '';

    resetToRoot(drawer);

    // openers
    $$(SEL.openers).forEach((opener) => {
      opener.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer(drawer, overlay);
      });
    });

    // delegated clicks
    document.addEventListener('click', (e) => {
      const t = e.target;

      if (t === overlay || t.closest?.(SEL.overlay)) {
        if (STATE.open) closeDrawer(drawer, overlay);
        return;
      }

      if (!drawer.contains(t)) return;

      const closeBtn = t.closest?.(SEL.closeBtn);
      if (closeBtn) {
        e.preventDefault();
        closeDrawer(drawer, overlay);
        return;
      }

      const openBtn = t.closest?.(SEL.openSub);
      if (openBtn) {
        const targetId = openBtn.getAttribute('data-submenu-target');
        if (!targetId) return;
        e.preventDefault();

        openBtn.setAttribute('aria-expanded', 'true');

        if (STATE.stack[STATE.stack.length - 1] !== targetId) {
          STATE.stack.push(targetId);
        }

        animatePush(drawer, targetId, 'forward');
        return;
      }

      const closeSub = t.closest?.(SEL.closeSub);
      if (closeSub) {
        const targetId = closeSub.getAttribute('data-submenu-target');
        if (!targetId) return;
        e.preventDefault();

        // trim stack to explicit target
        const idx = STATE.stack.lastIndexOf(targetId);
        if (idx >= 0) STATE.stack = STATE.stack.slice(0, idx + 1);
        else if (STATE.stack.length > 1) STATE.stack.pop();

        animatePush(drawer, targetId, 'back');
        return;
      }

      const link = t.closest?.('a[href]');
      if (link) {
        const newTab = link.getAttribute('target') === '_blank';
        if (!newTab) closeDrawer(drawer, overlay);
      }
    });

    // keys
    document.addEventListener('keydown', (e) => {
      if (!STATE.open) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        if (STATE.stack.length > 1) backOne(drawer);
        else closeDrawer(drawer, overlay);
        return;
      }

      focusTrap(drawer, e);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();